<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import type { MessageAttachmentDTO } from '@nora-monorepo/nora-api-client';
import {
  bgBlue,
  bgSecondary,
  isImage,
  isVideo,
  prettyPrintFileSize,
  textPrimary,
} from '@nora-monorepo/ui-shared';

import DeletedPreview from '#nora-chat-ui/components/open-conversations/messages/message-attachments/message-attachment-preview/DeletedPreview.vue';
import FilePreview from '#nora-chat-ui/components/open-conversations/messages/message-attachments/message-attachment-preview/FilePreview.vue';
import ImagePreview from '#nora-chat-ui/components/open-conversations/messages/message-attachments/message-attachment-preview/ImagePreview.vue';
import VideoPreview from '#nora-chat-ui/components/open-conversations/messages/message-attachments/message-attachment-preview/VideoPreview.vue';
import AttachmentGallery from '#nora-chat-ui/components/open-conversations/messages/message-attachments/message-attachment-view/AttachmentGallery.vue';
import { useAttachmentRefresh } from '#nora-chat-ui/composables/useAttachmentRefresh';
import { getExtension } from '#nora-chat-ui/utils/get-extension';

interface MessageAttachmentPreviewProps {
  attachment: MessageAttachmentDTO;
  hideMetaData?: boolean;
  isMyMessage?: boolean;
  maxSize?: number;
  conversationId: string;
  messageId: string;
}

const props = defineProps<MessageAttachmentPreviewProps>();

const hasError = ref(false);

const isDeleted = computed(() => Boolean(props.attachment.deletedAt));

const { watchAttachment, refreshConversationAttachment } = useAttachmentRefresh();

onMounted(() => {
  if (isDeleted.value) {
    return;
  }
  watchAttachment({ conversationId: props.conversationId, messageId: props.messageId, attachment: props.attachment });
});

async function handleAttachmentLoadError(): Promise<void> {
  await refreshConversationAttachment({ conversationId: props.conversationId, attachmentId: props.attachment.id });
}

const showMetadata = computed(() => {
  return (
    !hasError.value
    && !isDeleted.value
    && props.attachment.size
    && props.attachment.mimeType
    && isImage(props.attachment.mimeType)
  );
});

const size = computed(() => {
  return props.maxSize ?? 300;
});

const height = computed(() => {
  return size.value;
});

const width = computed(() => {
  return size.value;
});

const isAttachmentGalleryOpen = ref(false);
function openAttachmentGallery() {
  isAttachmentGalleryOpen.value = true;
}
function closeAttachmentGallery() {
  isAttachmentGalleryOpen.value = false;
}

const thumbnailUrl = computed(() => {
  if (!props.attachment.urls?.thumbnail?.url) {
    return '';
  }

  const loadWidth = width.value * 2;
  const loadHeight = height.value * 2;

  const url = new URL(props.attachment.urls.thumbnail.url);

  url.searchParams.set('height', loadHeight.toString());
  url.searchParams.set('width', loadWidth.toString());

  return url.toString();
});
</script>

<template>
  <div class="min-w-0">
    <AttachmentGallery
      v-if="isAttachmentGalleryOpen && !isDeleted"
      :attachment="props.attachment"
      @close="closeAttachmentGallery"
      @refresh-urls="handleAttachmentLoadError"
    />

    <div class="relative max-h-[300px] overflow-hidden rounded-sm">
      <DeletedPreview v-if="isDeleted" />
      <button
        v-else
        type="button"
        class="block w-full h-full cursor-pointer text-left"
        :title="attachment.fileName"
        @click="openAttachmentGallery"
      >
        <ImagePreview
          v-if="
            thumbnailUrl
              && !hasError
              && isImage(attachment.mimeType)
              && typeof attachment.fileId !== 'undefined'
          "
          :url="thumbnailUrl"
          :name="attachment.fileName"
          :max-size="size"
          :style="{
            height: `${height}px`,
            width: `${width}px`,
          }"
          @refresh-urls="handleAttachmentLoadError"
        />

        <VideoPreview
          v-else-if="
            thumbnailUrl
              && !hasError
              && isVideo(attachment.mimeType)
              && attachment.fileId
          "
          :url="thumbnailUrl"
          :name="attachment.fileName"
          :max-size="size"
          :style="{
            height: `${height}px`,
            width: `${width}px`,
          }"
          @refresh-urls="handleAttachmentLoadError"
        />

        <FilePreview
          v-else
          :attachment="attachment"
          :is-my-message="isMyMessage ?? false"
        />

        <div
          v-if="showMetadata"
          class="flex flex-row items-center absolute bottom-2 right-3 px-1 rounded-sm whitespace-nowrap"
          :class="[
            isMyMessage
              ? `${bgBlue} text-label-on-accent-primary text-white`
              : `${bgSecondary} ${textPrimary}`,
          ]"
        >
          <span v-if="!hideMetaData">
            {{
              `${prettyPrintFileSize(attachment.size)} | ${getExtension(
                attachment.mimeType,
              )?.toUpperCase()}`
            }}
          </span>
        </div>
      </button>
    </div>
  </div>
</template>
